import { z } from 'zod';
import { workspaceSchema } from './workshop.js';
import { activityCaptureSchema, excludedProjectFileSchema } from './workspace-runtime.js';
const digest = z.string().regex(/^[a-f0-9]{64}$/);
export const explanationDraftSchema = z
    .object({
    text: z.string().max(8000),
    revision: z.number().int().nonnegative(),
})
    .strict();
export const updateExplanationDraftSchema = z
    .object({
    text: z.string().max(8000),
    expectedRevision: z.number().int().nonnegative(),
})
    .strict();
export const checkpointPointerSchema = z
    .object({
    key: z.string().min(1).max(512),
    hash: digest,
    revision: z.number().int().positive(),
    operationId: z.uuid(),
    capturedAt: z.iso.datetime(),
})
    .strict();
export const workspaceCheckpointSchema = activityCaptureSchema
    .extend({
    environment: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    learnerId: z.uuid(),
    explanationDraft: explanationDraftSchema,
})
    .strict();
const attemptIdentity = z.object({ attemptId: z.uuid(), taskVersionId: z.uuid() }).strict();
export const workspaceJobRequestSchema = z
    .object({
    version: z.literal(1),
    action: z.enum(['activate', 'checkpoint', 'stop', 'submit']),
    expectedGeneration: z.number().int().positive(),
    saveMode: z.enum(['editor', 'disk']),
    maintenance: z
        .object({
        reason: z.enum([
            'periodic',
            'idle',
            'access_revoked',
            'unselected',
            'shutdown',
            'unhealthy',
        ]),
        observedActivityAt: z.iso.datetime(),
    })
        .strict()
        .optional(),
    source: attemptIdentity
        .extend({
        draft: explanationDraftSchema,
        checkpointRevision: z.number().int().positive(),
    })
        .strict()
        .nullable(),
    target: attemptIdentity.nullable(),
    editorSessionId: z.uuid().nullable(),
    evaluator: z
        .object({ modelId: z.string().min(1), version: z.string().min(1) })
        .strict()
        .nullable(),
    /**
     * A pause that only releases the selected activity: files are checkpointed and the
     * attempt is deselected, but the compute keeps running so Field can take it over at
     * once instead of waiting for a provider stop and start.
     */
    keepCompute: z.boolean().optional(),
})
    .strict()
    .superRefine((request, ctx) => {
    if (request.keepCompute && (request.action !== 'stop' || request.maintenance))
        ctx.addIssue({
            code: 'custom',
            path: ['keepCompute'],
            message: 'Only a learner pause may keep its compute',
        });
    if (request.action === 'activate' && !request.target)
        ctx.addIssue({
            code: 'custom',
            path: ['target'],
            message: 'Activation requires a target attempt',
        });
    if (request.maintenance &&
        (!['checkpoint', 'stop'].includes(request.action) ||
            (request.maintenance.reason === 'periodic'
                ? request.action !== 'checkpoint'
                : request.action !== 'stop') ||
            (request.maintenance.reason === 'unselected'
                ? request.source !== null
                : request.maintenance.reason !== 'shutdown' && request.source === null)))
        ctx.addIssue({
            code: 'custom',
            path: ['maintenance'],
            message: 'Maintenance may only save or stop the observed workspace',
        });
    if (request.action !== 'activate' &&
        !request.source &&
        !['unselected', 'shutdown', 'unhealthy'].includes(request.maintenance?.reason ?? ''))
        ctx.addIssue({
            code: 'custom',
            path: ['source'],
            message: 'This operation requires a selected activity',
        });
    if (request.action !== 'activate' && request.target)
        ctx.addIssue({
            code: 'custom',
            path: ['target'],
            message: 'Only activation may select another activity',
        });
    if ((request.action === 'submit') !== (request.evaluator !== null))
        ctx.addIssue({
            code: 'custom',
            path: ['evaluator'],
            message: 'Submission must pin its evaluator',
        });
});
const stopDispatchSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('not_dispatched') }).strict(),
    z
        .object({
        status: z.literal('rejected'),
        httpStatus: z
            .number()
            .refine((status) => [400, 401, 403, 404, 409, 422, 429].includes(status)),
    })
        .strict(),
    z
        .object({
        status: z.literal('uncertain'),
        previousBuildId: z.uuid().nullable(),
        dispatchedAt: z.iso.datetime(),
    })
        .strict(),
    z
        .object({
        status: z.literal('accepted'),
        buildId: z.uuid(),
        buildNumber: z.number().int().positive(),
        previousBuildId: z.uuid().nullable(),
    })
        .strict(),
]);
export const workspaceJobProgressSchema = z
    .object({
    phase: z.enum([
        'pending',
        'source_saved',
        'target_prepared',
        'committed',
        'editor_ready',
        'stopping',
        'done',
    ]),
    checkpoint: checkpointPointerSchema.nullable(),
    captureArtifactHash: digest.optional(),
    captureReleased: z.boolean().optional(),
    // Missing on legacy stopping jobs means dispatch is unknown, never safe to repeat.
    stopDispatch: stopDispatchSchema.optional(),
    // Absent on older operations; do not confuse unknown coverage with no exclusions.
    excluded: z.array(excludedProjectFileSchema).max(500).optional(),
    committedGeneration: z.number().int().positive().nullable(),
    submissionId: z.uuid().nullable(),
    /** A background backup a learner command absorbed before it started. */
    superseded: z.boolean().optional(),
})
    .strict();
export const workspaceCommandSchema = z
    .object({
    idempotencyKey: z.uuid(),
    expectedGeneration: z.number().int().positive(),
    expectedAttemptId: z.uuid().nullable(),
    editorSessionId: z.uuid().nullable(),
})
    .strict();
/**
 * A stop may name its save mode. `disk` pauses a Workshop without its editor so the learner
 * can switch to Field: files already saved to disk are checkpointed, and the worker refuses
 * while an editor is still connected. Checkpoints and submissions always certify buffers, so
 * they keep the plain command shape.
 */
export const workspacePauseCommandSchema = workspaceCommandSchema
    .extend({
    saveMode: z.enum(['editor', 'disk']).optional(),
    /** Release the activity but leave the compute running for a Field switch. */
    keepCompute: z.boolean().optional(),
})
    .strict();
export const workspaceOperationSchema = z.object({
    id: z.uuid(),
    // Older API versions omit this; clients must treat unknown work as disruptive.
    action: workspaceJobRequestSchema.shape.action.optional(),
    background: z.boolean().optional(),
    /** Started by maintenance (backup, idle or unhealthy stop), not by the learner. */
    maintenance: z.boolean().optional(),
    status: z.enum(['pending', 'running', 'succeeded', 'failed']),
    phase: workspaceJobProgressSchema.shape.phase,
    errorCode: z.string().nullable(),
    submissionId: z.uuid().nullable(),
    committedGeneration: z.number().int().positive().nullable(),
    capture: z
        .object({ attemptId: z.uuid(), excluded: z.array(excludedProjectFileSchema).max(500) })
        .nullable()
        .optional(),
});
export const currentWorkspaceSchema = z.object({
    enabled: z.boolean(),
    workspace: workspaceSchema.nullable(),
    activity: z
        .object({ id: z.uuid(), title: z.string(), code: z.string(), accessible: z.boolean() })
        .nullable(),
    draft: explanationDraftSchema.nullable(),
    checkpoint: checkpointPointerSchema
        .pick({ revision: true, capturedAt: true })
        .extend({ excluded: z.array(excludedProjectFileSchema).max(500).nullable().optional() })
        .nullable(),
    busyOperation: workspaceOperationSchema.nullable(),
});
